import React, { useState } from 'react'
import { User, Mail, Phone, MapPin, Lock, Eye, EyeOff, Save, Bell } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

const ProfilePage: React.FC = () => {
  const { user, secretUser, updateProfile, signOut } = useAuth()

  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [savingPreferences, setSavingPreferences] = useState(false)

  // Profile form state
  const [formData, setFormData] = useState({
    firstName: secretUser?.first_name || '',
    lastName: secretUser?.last_name || '',
    zipCode: secretUser?.zip_code || ''
  })

  // Notification preferences state
  const [preferences, setPreferences] = useState({
    emailNotifications: secretUser?.email_notifications ?? true,
    smsNotifications: secretUser?.sms_notifications ?? true,
    marketingEmails: secretUser?.marketing_emails ?? false
  })

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  // Update formData and preferences when secretUser loads
  React.useEffect(() => {
    if (secretUser) {
      setFormData({
        firstName: secretUser.first_name || '',
        lastName: secretUser.last_name || '',
        zipCode: secretUser.zip_code || ''
      })
      setPreferences({
        emailNotifications: secretUser.email_notifications ?? true,
        smsNotifications: secretUser.sms_notifications ?? true,
        marketingEmails: secretUser.marketing_emails ?? false
      })
    }
  }, [secretUser])

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile({
        first_name: formData.firstName,
        last_name: formData.lastName,
        zip_code: formData.zipCode
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Profile update error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault()

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.custom((t) => (
        <EnterpriseToast
          type="error"
          title="Passwords Don't Match"
          message="Your new passwords must match"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
      return
    }

    if (passwordData.newPassword.length < 8) {
      toast.custom((t) => (
        <EnterpriseToast
          type="error"
          title="Password Too Short"
          message="Password must be at least 8 characters long"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
      return
    }

    setChangingPassword(true)

    // Fire password update and immediately redirect - don't wait for anything
    supabase.auth.updateUser({ password: passwordData.newPassword })

    // Clear all auth storage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase')) {
        localStorage.removeItem(key)
      }
    })

    // Force hard reload to login
    window.location.replace('/login')
  }

  const handlePreferencesUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingPreferences(true)

    try {
      const { error } = await supabase
        .from('secret_users')
        .update({
          email_notifications: preferences.emailNotifications,
          sms_notifications: preferences.smsNotifications,
          marketing_emails: preferences.marketingEmails
        })
        .eq('id', secretUser?.id)

      if (error) throw error

      toast.custom((t) => (
        <EnterpriseToast
          type="success"
          title="Preferences Saved"
          message="Your notification preferences have been updated"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } catch (error) {
      console.error('Failed to update preferences:', error)
      toast.custom((t) => (
        <EnterpriseToast
          type="error"
          title="Update Failed"
          message="Could not save your preferences. Please try again"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } finally {
      setSavingPreferences(false)
    }
  }

  const cancelEdit = () => {
    setFormData({
      firstName: secretUser?.first_name || '',
      lastName: secretUser?.last_name || '',
      zipCode: secretUser?.zip_code || ''
    })
    setIsEditing(false)
  }

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-foreground mb-8">Profile Settings</h1>

        {/* Account Information Card */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Account Information</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 text-sm font-medium border border-[#962831] rounded-md hover:bg-[#962831]/10 transition-colors"
              >
                <span style={{ color: '#962831' }}>Edit Profile</span>
              </button>
            )}
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-6">
            {/* Email (read-only) - only show if user has email */}
            {user?.email && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="block w-full pl-10 pr-3 py-3 border border-input rounded-md cursor-not-allowed"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#1f2937',
                      WebkitTextFillColor: '#1f2937',
                      opacity: 1
                    }}
                  />
                </div>
                {secretUser?.email_verified && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ Verified</p>
                )}
              </div>
            )}

            {/* Phone (read-only) - only show if user has phone */}
            {user?.phone && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <input
                    type="tel"
                    value={user.phone}
                    disabled
                    className="block w-full pl-10 pr-3 py-3 border border-input rounded-md cursor-not-allowed"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#1f2937',
                      WebkitTextFillColor: '#1f2937',
                      opacity: 1
                    }}
                  />
                </div>
                {secretUser?.phone_verified && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ Verified</p>
                )}
              </div>
            )}

            {/* First Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    disabled={!isEditing}
                    className="block w-full pl-10 pr-3 py-3 border border-input rounded-md bg-background text-foreground disabled:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  disabled={!isEditing}
                  className="block w-full px-3 py-3 border border-input rounded-md bg-background text-foreground disabled:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Zip Code */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Zip Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  disabled={!isEditing}
                  className="block w-full pl-10 pr-3 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  style={!isEditing ? {
                    backgroundColor: '#f3f4f6',
                    color: '#1f2937',
                    WebkitTextFillColor: '#1f2937',
                    opacity: 1
                  } : undefined}
                />
              </div>
            </div>

            {/* Save/Cancel buttons */}
            {isEditing && (
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border border-border text-foreground rounded-md hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Notification Preferences Card */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-6">Notification Preferences</h2>

          <form onSubmit={handlePreferencesUpdate} className="space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground">Email Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Receive order updates and important information via email
                  </p>
                  {!user?.email && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Add an email address to enable email notifications
                    </p>
                  )}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.emailNotifications}
                  onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
                  disabled={!user?.email}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              </label>
            </div>

            {/* SMS Notifications */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground">SMS Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Get text message alerts for order status changes
                  </p>
                  {!user?.phone && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Add a phone number to enable SMS notifications
                    </p>
                  )}
                  {user?.phone && !secretUser?.phone_verified && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Verify your phone number to enable SMS notifications
                    </p>
                  )}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.smsNotifications}
                  onChange={(e) => setPreferences({ ...preferences, smsNotifications: e.target.checked })}
                  disabled={!user?.phone || !secretUser?.phone_verified}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              </label>
            </div>

            {/* Marketing Emails */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start space-x-3">
                <Bell className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground">Marketing & Promotions</h3>
                  <p className="text-sm text-muted-foreground">
                    Receive news about special offers, new menu items, and upcoming events
                  </p>
                  {!user?.email && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Add an email address to enable marketing emails
                    </p>
                  )}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.marketingEmails}
                  onChange={(e) => setPreferences({ ...preferences, marketingEmails: e.target.checked })}
                  disabled={!user?.email}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              </label>
            </div>

            {/* Save Preferences Button */}
            <button
              type="submit"
              disabled={savingPreferences}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {savingPreferences ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Preferences
                </>
              )}
            </button>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-6">Change Password</h2>

          <form onSubmit={handlePasswordChange} className="space-y-6">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="block w-full pl-10 pr-10 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter new password (min. 8 characters)"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.new ? (
                    <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  ) : (
                    <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="block w-full pl-10 pr-10 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Re-enter new password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.confirm ? (
                    <EyeOff className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  ) : (
                    <Eye className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                  )}
                </button>
              </div>
            </div>

            {/* Update Password Button */}
            <button
              type="submit"
              disabled={changingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {changingPassword ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  Update Password
                </>
              )}
            </button>
          </form>
        </div>

        {/* Account Details */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Account created:</strong> {new Date(secretUser?.created_at || '').toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
